import { Client } from "@/Common/Api/Client";
import { ColumnClickArgs, MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { GridDisplaySettings } from "@/Common/Models/UI/Tables/GridDisplaySettings";
import { ITableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import { TableColumnInfo } from "@/Common/Models/UI/Tables/TableColumnInfo";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { IconButton } from "../IconControls/IconControls";
import { CustomTable2 } from "./CustomTable2";

interface IMyleTableProps<TItem extends ITableDataSourceItem<any>> {
    TableId: string;
    TableDataCy?: string;
    DataSource: Immutable.List<TItem>;
    Columns: Immutable.List<MyleTableColumn>;
    TableRenderOptions: ITableRenderOptions;
    RowClickTarget?: MyleTableTarget;
    RowClickOperation?: number;
    RowClickFunction?: (rowDataModel: TItem, mouseEvent: React.MouseEvent) => void;
    ColumnReorderTarget?: MyleTableTarget;
    ColumnSortTarget?: MyleTableTarget;
    ColumnSortDirection?: string;
    ShouldResizeTable?: boolean;
    ColumnsPreferencesChangedOperation?: number; // Only Resize for Now
    SaveOptionskeyTarget?: string;
    CssClass?: string;
    UnclickableColumnIndex?: number;
    PosRefClassName?: string;
    DefaultSelectedItem?: TItem; // to have a default selected item on load
    NoScrollLimit?: boolean; // fix the scrollable height of the limit
    HideFooter?: boolean;
    ShowHeaderTextInBold?: boolean;
    isDraggable?: boolean;

    IsActive?: (item: any) => boolean; // Jf the active state has to be controlled by external source, this will take priority. Keep null to ignore.
    IsRowLocked?: (item: any) => boolean;
    onColumnPreferenceChange?: () => void;
    onDragStart?: (item: TItem) => void;
    onDrop?: (item: TItem) => void;

    // Outpout a <tr> element below each row. (Usually for cross-column footers per row)
    rowRendering?: (item: TItem, isLocked: boolean, mainTr: () => JSX.Element) => JSX.Element[];
}

class MyleTableState {
    public selectedItemHash: number;
    public unlockedRowsHashes: number[] = [];
}

class MyleTable<TItem extends ITableDataSourceItem<any>> extends React.Component<IMyleTableProps<TItem>, MyleTableState> {
    private tableReference: CustomTable2;

    constructor(props: IMyleTableProps<TItem>) {
        super(props);

        const initialState = new MyleTableState();
        if (this.props.DefaultSelectedItem != null)
            initialState.selectedItemHash = MyleHelper.getHash(this.props.DefaultSelectedItem.Model);

        this.state = initialState;
    }

    public componentDidMount(): void {
        window.setTimeout(
            () => this.tableReference = new CustomTable2(
                this.props.TableId,
                this.props.TableRenderOptions,
                (context, userResized) => this.onColumnChanged(context, userResized, this),
                this.props.PosRefClassName
            ),
            100
        );
    }

    public componentWillReceiveProps(nextProps: IMyleTableProps<TItem>): void {
        let state = null;

        if (this.props.DefaultSelectedItem != nextProps.DefaultSelectedItem && this.props.DefaultSelectedItem != null) {
            state = new MyleTableState();
            if (nextProps.DefaultSelectedItem != null)
                state.selectedItemHash = MyleHelper.getHash(nextProps.DefaultSelectedItem.Model);
        }

        this.setState({ ...state, unlockedRowsHashes: [] });
    }

    public componentDidUpdate(): void {
        if (this.tableReference) {
            this.tableReference.destroy();
            window.setTimeout(
                () => {
                    this.tableReference = new CustomTable2(
                        this.props.TableId,
                        this.props.TableRenderOptions,
                        (context, userResized) => this.onColumnChanged(context, userResized, this),
                        this.props.PosRefClassName
                    );
                },
                100
            );
        }
    }

    public componentWillUnmount(): void {
        if (this.tableReference != null) {
            try {
                this.tableReference.destroy();
            }
            catch {
            }
        }
    }

    public render(): JSX.Element {
        if (this.props.DataSource == null)
            return <div />;

        const columnsToShow = this.props.Columns.filter(col => col.columnOptions.IsHidden === false).toArray();

        return (
            <div id={this.props.TableId} data-cy={this.props.TableDataCy} className={`tb-main-container ${this.props.CssClass || ""}`}>
                <div className="header">
                    <table>
                        <thead>
                            <tr>
                                {columnsToShow.map((item, index) => {
                                    const clickArgs = new ColumnClickArgs({
                                        target: this.props.ColumnSortTarget,
                                        item: item
                                    });

                                    return (
                                        <th
                                            data-sw={item.columnOptions.WidthPixels}
                                            data-header={item.columnOptions.FieldName}
                                            data-cy={item.columnOptions.DataCy}
                                            key={index + "_header"}
                                            className={`${item.columnOptions.IsSortable ? "column-sortable" : ""} ${this.props.ShowHeaderTextInBold ? "bold-header-columns" : ""}`}
                                        >
                                            {item.renderHeader(clickArgs)}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                    </table>
                </div>

                <div className={`content ${!this.props.NoScrollLimit ? "scroll-limit" : ""}`}>
                    <table className={`myle-table table ${this.props.HideFooter ? " no-table-footer" : ""}`}>
                        {this.props.DataSource.map((row, rowIndex) => {
                            const rowKey = rowIndex + "_tb_row";

                            let isActive = this.props.IsActive
                                ? this.props.IsActive(row)
                                : MyleHelper.getHash(row.Model ?? row) == this.state.selectedItemHash;

                            const isLocked = this.props.IsRowLocked?.(row);
                            const showEyeSlashIcon = isLocked && !this.state.unlockedRowsHashes.any(p => p === MyleHelper.getHash(row.Model ?? row));

                            const cssClass = `myle-table-row ${isActive ? "active" : ""} ${showEyeSlashIcon ? "row-locked-opacity" : ""} ${isLocked ? "row-locked" : ""}`.trim();

                            return (
                                <tbody
                                    key={rowKey}
                                    data-row-id={rowIndex}
                                    className={cssClass}
                                    draggable={!!this.props.isDraggable}
                                    onDrag={() => this.props.onDragStart?.(row)}
                                    onDrop={() => this.props.onDrop?.(row)}
                                    onDragOver={event => event.preventDefault()}
                                >
                                    {isLocked && showEyeSlashIcon && (
                                        <tr>
                                            <td
                                                colSpan={0}
                                                className="td-locked-wrapper"
                                            >
                                                <IconButton
                                                    type="eye-slash"
                                                    color="lightblue"
                                                    buttonClassName="row-locked-icon-button"
                                                    onClick={() => this.removeOpacityLockedRow(row)}
                                                />
                                            </td>
                                        </tr>
                                    )}

                                    {!this.props.rowRendering
                                        ? this.renderTR(rowIndex, row, columnsToShow, isLocked)
                                        : this.props.rowRendering(row.Model, isLocked, () => this.renderTR(rowIndex, row, columnsToShow, isLocked))
                                    }
                                </tbody>
                            );
                        })}
                    </table>
                </div>
            </div>
        );
    }

    private renderTR(rowIndex: number, row: TItem, columnsToShow: MyleTableColumn[], isLocked: boolean): JSX.Element {
        return (
            <tr key="main">
                {columnsToShow.map((col, colIndex) => {
                    const wrap = col.columnOptions.DefaultWrapBehavior === true ? "wrap" : "";
                    const cellKey = `r${rowIndex}c${colIndex}`;

                    const canClick = !isLocked && col.columnOptions.RowClickEnabled && colIndex !== this.props.UnclickableColumnIndex;

                    return (
                        <td
                            className={`${wrap} ${col.columnOptions.columnClass} vertical-align-middle ${canClick ? "pointer" : ""}`}
                            key={cellKey + colIndex}
                            onClick={e => canClick && this.onRowClick(row, e)}
                        >
                            {col.renderCell(row)}
                        </td>
                    );
                })}
            </tr>
        );
    }

    private removeOpacityLockedRow = (row: any): void => {
        const rowHash = MyleHelper.getHash(row.Model ?? row);
        const newClearedRowHashes = this.state.unlockedRowsHashes.concat([rowHash]);

        this.setState({
            unlockedRowsHashes: newClearedRowHashes
        });
    };

    public shouldComponentUpdate(nextProps: IMyleTableProps<TItem>, nextState: MyleTableState): boolean {
        return nextProps.DataSource !== this.props.DataSource
            || nextProps.RowClickTarget !== this.props.RowClickTarget
            || nextProps.ColumnReorderTarget !== this.props.ColumnReorderTarget
            || nextProps.ColumnSortTarget !== this.props.ColumnSortTarget
            || nextProps.ColumnSortDirection !== this.props.ColumnSortDirection
            || this.columnObjectUpdated(this.props.Columns, nextProps.Columns)
            || this.state.selectedItemHash !== nextState.selectedItemHash
            || this.state.unlockedRowsHashes.length !== nextState.unlockedRowsHashes.length;
    }

    private columnObjectUpdated(currentCols: Immutable.List<MyleTableColumn>, nextCols: Immutable.List<MyleTableColumn>): boolean {
        if (currentCols.size !== nextCols.size)
            return true;

        for (let i = 0; i < currentCols.size; i++) {
            const curOptions = currentCols.get(i).columnOptions;
            const nextOptions = nextCols.get(i).columnOptions;

            if (curOptions.FieldName !== nextOptions.FieldName || curOptions.IsHidden !== nextOptions.IsHidden)
                return true;
        }

        return false;
    }

    /**
     * This function is invoked every time there is a change to column proportions either because user resized columns
     * or simply because columns were re-adjusted due to window resize.
     *
     * @param context CustomTable object is returned
     * @param userResized True only when user resizes coluimns manually and now when columns are resized due to window resize
     */
    private onColumnChanged(context: CustomTable2, userResized: boolean, componentContext: MyleTable<TItem>): void {
        if (!userResized)
            return;

        const cols = context.getColumnRatios();

        // new way of saving table options
        if (this.props.SaveOptionskeyTarget != null) {
            const settings = new UserSettings({
                GridsDisplaySettings: []
            });

            {
                const gridDisplaySettings = new KeyValuePair<string, GridDisplaySettings>(this.props.SaveOptionskeyTarget, new GridDisplaySettings());
                gridDisplaySettings.Value.OrderedColumns = [];

                const columns = this.props.Columns.toArray();
                for (let column of columns) {
                    const t = new TableColumnInfo();
                    const cfg = column.columnOptions;
                    t.ColumnName = cfg.FieldName;
                    t.IsHidden = cfg.IsHidden;
                    t.WidthRatio = cols.filter(col => col.Key == cfg.FieldName)?.[0]?.Value ?? 0;
                    gridDisplaySettings.Value.OrderedColumns.push(t);
                }

                settings.GridsDisplaySettings.push(gridDisplaySettings);
            }

            // save current grid to DB
            const client = new Client();
            client.Post(
                apiSettings.buildApiPath(),
                ServerOperations.Server.Myle_UpdateUserSettings,
                { UserSettings: settings, UserId: sessionStore.userId },
                () => {}, // on success
                () => {} // on error
            );

            // add others and update cache
            if (cacheStore.UserSettings == null)
                cacheStore.UserSettings = new UserSettings();
            else {
                for (let setting of cacheStore.UserSettings.GridsDisplaySettings) {
                    if (setting.Key != this.props.SaveOptionskeyTarget)
                        settings.GridsDisplaySettings.push(setting);
                }
            }
            cacheStore.UserSettings.GridsDisplaySettings = settings.GridsDisplaySettings;

            this.props.onColumnPreferenceChange?.();
        }

        // old way
        else {
            sessionStore.dispatch(
                componentContext.props.ColumnsPreferencesChangedOperation,
                {
                    ratios: cols,
                    SaveOptionskey: this.props.SaveOptionskeyTarget
                }
            );
        }
    }

    private onRowClick(tableDataSourceItem: TItem, e: React.MouseEvent): void {
        // deselect all other rows
        $(e.target).closest("tr").parent().parent().children().children().removeClass("active");

        // select self -- dispatched action below will not re-select the last row in cases where we click the same row twice.
        $(e.target).closest("tr").addClass("active");

        let s = new MyleTableState();
        s.selectedItemHash = MyleHelper.getHash(tableDataSourceItem.Model);
        this.setState(s);

        if (this.props.RowClickOperation != null) {
            sessionStore.dispatch(
                this.props.RowClickOperation,
                {
                    target: this.props.RowClickTarget,
                    event: e,
                    dataSourceItem: tableDataSourceItem,
                    rowData: tableDataSourceItem.Model
                }
            );
        }

        this.props.RowClickFunction?.(tableDataSourceItem, e);
    }

    public static LoadDefaultTableRenderOptions(ColumnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: ColumnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null,
            HeightOffset: 38
        });
    }
}

export { MyleTable };
